import React from 'react';
import { Car, Truck, Bus, Wrench, Key, Calendar, CheckCircle, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';

const VehiclesLogistics = () => {
  const { language } = useLanguage();

  const vehicles = [
    {
      icon: Car,
      title: { fr: 'Voitures de Fonction', en: 'Company Cars' },
      description: { fr: 'Véhicules légers pour déplacements professionnels', en: 'Light vehicles for business travel' },
      image: 'https://images.unsplash.com/photo-1587813369290-091c9d432daf',
      features: [
        { fr: 'Berlines et SUV', en: 'Sedans and SUVs' },
        { fr: 'Essence et diesel', en: 'Gasoline and diesel' },
        { fr: 'Options hybrides disponibles', en: 'Hybrid options available' },
        { fr: '5 à 7 places', en: '5 to 7 seats' },
        { fr: 'Climatisation standard', en: 'Standard air conditioning' }
      ]
    },
    {
      icon: Truck,
      title: { fr: 'Camions de Livraison', en: 'Delivery Trucks' },
      description: { fr: 'Véhicules utilitaires pour transport de marchandises', en: 'Utility vehicles for goods transport' },
      image: 'https://images.unsplash.com/photo-1751218682008-f68d3eff2d7c',
      features: [
        { fr: 'Fourgons 3m³ à 20m³', en: 'Vans 3m³ to 20m³' },
        { fr: 'Camions frigorifiques', en: 'Refrigerated trucks' },
        { fr: 'Hayon élévateur disponible', en: 'Liftgate available' },
        { fr: 'GPS intégré', en: 'Integrated GPS' },
        { fr: 'Charge utile jusqu\'3.5T', en: 'Payload up to 3.5T' }
      ]
    },
    {
      icon: Bus,
      title: { fr: 'Bus et Minibus', en: 'Buses and Minibuses' },
      description: { fr: 'Transport de personnel et passagers', en: 'Staff and passenger transport' },
      image: 'https://images.unsplash.com/photo-1756723701257-46513cd36fc1',
      features: [
        { fr: 'Minibus 15 à 25 places', en: 'Minibuses 15 to 25 seats' },
        { fr: 'Bus 30 à 50 places', en: 'Buses 30 to 50 seats' },
        { fr: 'Climatisation puissante', en: 'Powerful air conditioning' },
        { fr: 'Accessibilité PMR', en: 'Wheelchair accessible' },
        { fr: 'Système audio', en: 'Audio system' }
      ]
    },
    {
      icon: Truck,
      title: { fr: 'Véhicules de Chantier', en: 'Construction Vehicles' },
      description: { fr: 'Équipements pour travaux et construction', en: 'Equipment for work and construction' },
      image: 'https://images.unsplash.com/photo-1513828583688-c52646db42da',
      features: [
        { fr: 'Pick-up 4x4', en: '4x4 Pick-ups' },
        { fr: 'Camions-bennes', en: 'Dump trucks' },
        { fr: 'Engins de levage', en: 'Lifting equipment' },
        { fr: 'Véhicules tout-terrain', en: 'Off-road vehicles' },
        { fr: 'Robustes et fiables', en: 'Robust and reliable' }
      ]
    }
  ];

  const services = [
    {
      icon: Key,
      title: { fr: 'Vente de Véhicules', en: 'Vehicle Sales' },
      description: { fr: 'Achat de véhicules neufs et occasions', en: 'Purchase of new and used vehicles' },
      features: [
        { fr: 'Garantie constructeur', en: 'Manufacturer warranty' },
        { fr: 'Financement disponible', en: 'Financing available' },
        { fr: 'Reprise de votre ancien véhicule', en: 'Trade-in your old vehicle' }
      ]
    },
    {
      icon: Calendar,
      title: { fr: 'Location Longue Durée', en: 'Long-Term Rental' },
      description: { fr: 'Location avec ou sans option d\'achat', en: 'Rental with or without purchase option' },
      features: [
        { fr: 'Contrats flexibles 12-60 mois', en: 'Flexible 12-60 month contracts' },
        { fr: 'Maintenance incluse', en: 'Maintenance included' },
        { fr: 'Assistance 24/7', en: '24/7 assistance' }
      ]
    },
    {
      icon: Wrench,
      title: { fr: 'Gestion de Flotte', en: 'Fleet Management' },
      description: { fr: 'Gestion complète de votre parc automobile', en: 'Complete management of your vehicle fleet' },
      features: [
        { fr: 'Suivi GPS en temps réel', en: 'Real-time GPS tracking' },
        { fr: 'Planification maintenance', en: 'Maintenance scheduling' },
        { fr: 'Rapports de gestion', en: 'Management reports' }
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      <section className="relative py-32 overflow-hidden" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1629019896873-cc95f1a8845d)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 to-blue-900/90"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-6">
            <Truck className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {language === 'fr' ? 'Véhicules & Logistique' : 'Vehicles & Logistics'}
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            {language === 'fr' ? 'Solutions de mobilité complètes pour votre entreprise' : 'Complete mobility solutions for your business'}
          </p>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Notre Gamme de Véhicules' : 'Our Vehicle Range'}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {vehicles.map((vehicle, index) => {
              const Icon = vehicle.icon;
              return (
                <Card key={index} className="overflow-hidden hover:shadow-2xl transition-all duration-300 bg-white">
                  <div className="relative h-64 overflow-hidden">
                    <img src={vehicle.image} alt={vehicle.title[language]} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-14 h-14 bg-white/90 backdrop-blur-sm rounded-lg flex items-center justify-center">
                        <Icon className="h-7 w-7 text-blue-600" />
                      </div>
                    </div>
                  </div>

                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{vehicle.title[language]}</h3>
                    <p className="text-gray-600 mb-6">{vehicle.description[language]}</p>
                    <ul className="space-y-3">
                      {vehicle.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-700">
                          <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                          <span>{feature[language]}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Nos Services' : 'Our Services'}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="p-8 bg-gradient-to-br from-blue-50 to-slate-50 border-2 border-gray-200 hover:border-blue-500 transition-all">
                  <div className="w-14 h-14 bg-blue-600 rounded-full flex items-center justify-center mb-4">
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title[language]}</h3>
                  <p className="text-gray-600 mb-4">{service.description[language]}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-700">
                        <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{feature[language]}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {language === 'fr' ? 'Besoin d\'une Solution de Mobilité ?' : 'Need a Mobility Solution?'}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {language === 'fr' ? 'Nos conseillers vous accompagnent dans le choix de vos véhicules' : 'Our advisors support you in choosing your vehicles'}
          </p>
          <a href="/contact">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-lg inline-flex items-center">
              {language === 'fr' ? 'Demander un Devis' : 'Request a Quote'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default VehiclesLogistics;